import { useNavigate } from 'react-router-dom';

import { Button, ButtonVariation } from '@/components/Button';
import { ChevronDirection, ChevronIcon } from '@/components/Icon';

import './ContractFormActions.css';
import { ROUTES } from '@/constants';

const DEFAULT_BACK_LABEL = 'Back to Manage Contracts';
const DEFAULT_DISCARD_LABEL = 'Discard Changes';
const DEFAULT_SAVE_LABEL = 'Save Changes';

export interface ContractFormActionConfig {
  /** When false, the button is not rendered (discard/save only, when isEditable is true). */
  visible?: boolean;
  disabled?: boolean;
  label?: string;
  'data-testid'?: string;
}

export interface ContractFormBackActionConfig {
  /** Defaults to navigating to /contract/all. */
  onClick?: () => void;
  label?: string;
  'data-testid'?: string;
}

export interface ContractFormSubmitActionConfig extends ContractFormActionConfig {
  onClick: () => void;
}

export interface ContractFormActionsProps {
  'data-testid'?: string;
  className?: string;
  /** When false, discard and save are not rendered. Back is always shown. Defaults to true. */
  isEditable?: boolean;
  back?: ContractFormBackActionConfig;
  discard?: ContractFormSubmitActionConfig;
  save?: ContractFormSubmitActionConfig;
}

function showEditableAction(
  isEditable: boolean,
  config: ContractFormSubmitActionConfig | undefined,
): config is ContractFormSubmitActionConfig {
  if (!isEditable || config === undefined) {
    return false;
  }

  return config.visible !== false;
}

export function ContractFormActions({
  'data-testid': dataTestId,
  className,
  isEditable = true,
  back,
  discard,
  save,
}: ContractFormActionsProps) {
  const navigate = useNavigate();

  const handleBack =
    back?.onClick ??
    (() => {
      void navigate(ROUTES.MANAGE_CONTRACT);
    });

  const showDiscard = showEditableAction(isEditable, discard);
  const showSave = showEditableAction(isEditable, save);

  const rootClassName = ['contract-form-actions', className].filter(Boolean).join(' ');

  return (
    <div
      className={rootClassName}
      data-testid={dataTestId}>
      <div className='contract-form-actions__slot contract-form-actions__slot--back'>
        <Button
          isAutoWidth
          className='contract-form-actions__back'
          data-testid={back?.['data-testid']}
          type='button'
          variation={ButtonVariation.GHOST}
          onClick={handleBack}>
          <ChevronIcon
            direction={ChevronDirection.LEFT}
            size={14}
          />
          {back?.label ?? DEFAULT_BACK_LABEL}
        </Button>
      </div>
      {showDiscard && (
        <div className='contract-form-actions__slot contract-form-actions__slot--discard'>
          <Button
            isAutoWidth
            data-testid={discard['data-testid']}
            disabled={discard.disabled}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={discard.onClick}>
            {discard.label ?? DEFAULT_DISCARD_LABEL}
          </Button>
        </div>
      )}
      {showSave && (
        <div className='contract-form-actions__slot contract-form-actions__slot--save'>
          <Button
            isAutoWidth
            data-testid={save['data-testid']}
            disabled={save.disabled}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={save.onClick}>
            {save.label ?? DEFAULT_SAVE_LABEL}
          </Button>
        </div>
      )}
    </div>
  );
}
